"use client";

import { useCallback, useMemo } from "react";
import { useAuth } from "@/modules/auth/context/UserContext";
import { useDashboard } from "@/modules/dashboard/context/DashboardContext";
import { usePage } from "@/modules/pages/context/PageContext";
import { gtagEvent } from "../lib/gtag";
import {
  fireWidgetInteraction,
  fireReportExport,
  fireDealerChanged,
  fireDateRangeChanged,
  fireWidgetCreated,
  fireAiInsightsOpened,
  firePageCreated,
  fireLogin,
} from "../events";
import type {
  GaEventParams,
  WidgetInteractionParams,
  ReportExportParams,
  DealerChangedParams,
  DateRangeChangedParams,
  WidgetCreatedParams,
  AiInsightsOpenedParams,
  PageCreatedParams,
  LoginEventParams,
} from "../types";

interface SharedAnalyticsContext {
  page_id: string;
  page_name: string;
  dealer_id: string | undefined;
  dealer_name: string | undefined;
  user_id: string;
  user_name: string;
}

interface UseAnalyticsReturn {
  /** Fire any GA4 event with current page / user / dealer context auto-merged. */
  track: (eventName: string, params?: GaEventParams) => void;
  /** Strongly-typed event fire functions — caller supplies all fields. */
  events: {
    widgetInteraction: (params: WidgetInteractionParams) => void;
    reportExport: (params: ReportExportParams) => void;
    dealerChanged: (params: DealerChangedParams) => void;
    dateRangeChanged: (params: DateRangeChangedParams) => void;
    widgetCreated: (params: WidgetCreatedParams) => void;
    aiInsightsOpened: (params: AiInsightsOpenedParams) => void;
    pageCreated: (params: PageCreatedParams) => void;
    login: (params: LoginEventParams) => void;
  };
  /** Snapshot of shared context injected by `track()`. */
  context: SharedAnalyticsContext;
}

export function useAnalytics(): UseAnalyticsReturn {
  const { user } = useAuth();
  const { state: dashboardState } = useDashboard();
  const { state: pageState } = usePage();

  const context = useMemo<SharedAnalyticsContext>(
    () => ({
      page_id: pageState.currentPage ? String(pageState.currentPage.id) : "",
      page_name: pageState.currentPage?.title ?? "",
      dealer_id: dashboardState.selectedDealer
        ? dashboardState.selectedDealer.dealerRefId
        : undefined,
      dealer_name: dashboardState.selectedDealer?.dealerName,
      user_id: user?.userId ?? "",
      user_name: user?.userName ?? "",
    }),
    [pageState.currentPage, dashboardState.selectedDealer, user]
  );

  const track = useCallback(
    (eventName: string, params: GaEventParams = {}): void => {
      gtagEvent(eventName, { ...context, ...params });
    },
    [context]
  );

  const events = useMemo(
    () => ({
      widgetInteraction: fireWidgetInteraction,
      reportExport: fireReportExport,
      dealerChanged: fireDealerChanged,
      dateRangeChanged: fireDateRangeChanged,
      widgetCreated: fireWidgetCreated,
      aiInsightsOpened: fireAiInsightsOpened,
      pageCreated: firePageCreated,
      login: fireLogin,
    }),
    []
  );

  return { track, events, context };
}
